import { useEffect, useState, useCallback } from 'react'
import { api } from '../../lib/api'
import { JobCreator } from './JobCreator'
import { DiscoveryResults } from './DiscoveryResults'
import { ProvisioningStatus } from './ProvisioningStatus'
import { BulkPasswordChange } from './BulkPasswordChange'

interface ProvisioningJob {
  id: string
  site_id: string
  profile_id: string
  profile_name: string
  site_name: string
  status: string
  total_devices: number
  completed_devices: number
  failed_devices: number
  started_at: string | null
  completed_at: string | null
  created_at: string
}

interface SiteProvisioningTabProps {
  siteId: string
}

export function SiteProvisioningTab({ siteId }: SiteProvisioningTabProps) {
  const [jobs, setJobs] = useState<ProvisioningJob[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [viewingJobId, setViewingJobId] = useState<string | null>(null)
  const [showBulkPassword, setShowBulkPassword] = useState(false)

  const loadJobs = useCallback(async () => {
    try {
      const res = await api.get<{ data: ProvisioningJob[] }>(`/provisioning-jobs?site_id=${siteId}`)
      setJobs(res.data)
    } catch { /* ignore */ }
    finally { setLoading(false) }
  }, [siteId])

  useEffect(() => { loadJobs() }, [loadJobs])

  if (creating) {
    return <JobCreator siteId={siteId} onClose={() => { setCreating(false); loadJobs() }} />
  }

  if (showBulkPassword) {
    return <BulkPasswordChange siteId={siteId} onClose={() => setShowBulkPassword(false)} />
  }

  if (viewingJobId) {
    const job = jobs.find(j => j.id === viewingJobId)
    if (job && (job.status === 'provisioning' || job.status === 'completed' || job.status === 'failed')) {
      return <ProvisioningStatus jobId={viewingJobId} onClose={() => { setViewingJobId(null); loadJobs() }} />
    }
    return <DiscoveryResults jobId={viewingJobId} onClose={() => { setViewingJobId(null); loadJobs() }} />
  }

  const statusStyle = (status: string): React.CSSProperties => {
    const colors: Record<string, { bg: string; fg: string }> = {
      pending: { bg: 'rgba(160,160,160,0.1)', fg: 'var(--text-tertiary)' },
      discovering: { bg: 'var(--info-m)', fg: 'var(--info)' },
      discovered: { bg: 'var(--info-m)', fg: 'var(--info)' },
      matching: { bg: 'rgba(168,85,247,0.1)', fg: '#a855f7' },
      matched: { bg: 'rgba(168,85,247,0.1)', fg: '#a855f7' },
      provisioning: { bg: 'var(--warn-m)', fg: 'var(--warn)' },
      completed: { bg: 'var(--ok-m)', fg: 'var(--ok)' },
      failed: { bg: 'var(--err-m)', fg: 'var(--err)' },
    }
    const c = colors[status] || colors.pending
    return { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: c.bg, color: c.fg }
  }

  function formatDuration(start: string | null, end: string | null): string {
    if (!start) return ''
    const s = new Date(start).getTime()
    const e = end ? new Date(end).getTime() : Date.now()
    const mins = Math.round((e - s) / 60000)
    return mins < 60 ? `${mins} minutes` : `${Math.floor(mins / 60)}h ${mins % 60}m`
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>Provisioning Jobs</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setShowBulkPassword(true)}
            style={{
              padding: '7px 14px', fontSize: 12, fontWeight: 500,
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text-secondary)',
            }}
          >
            Bulk Password Change
          </button>
          <button
            onClick={() => setCreating(true)}
            style={{
              padding: '7px 14px', fontSize: 13, fontWeight: 600,
              background: 'var(--primary)', color: '#fff', border: 'none',
              borderRadius: 'var(--radius)', cursor: 'pointer',
            }}
          >
            + New Job
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>Loading...</div>
      ) : jobs.length === 0 ? (
        <div
          style={{
            padding: 40, textAlign: 'center',
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
          }}
        >
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 12 }}>
            No provisioning jobs for this site yet.
          </p>
          <button
            onClick={() => setCreating(true)}
            style={{
              padding: '8px 16px', fontSize: 13, fontWeight: 600,
              background: 'var(--primary)', color: '#fff', border: 'none',
              borderRadius: 'var(--radius)', cursor: 'pointer',
            }}
          >
            Create First Job
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {jobs.map((job, i) => (
            <div
              key={job.id}
              style={{
                padding: '16px 20px',
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                boxShadow: 'var(--shadow-xs)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                      Job #{jobs.length - i}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                      {new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <span style={statusStyle(job.status)}>
                      {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>
                    Profile: {job.profile_name || 'Unknown'}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                    {job.status === 'completed' ? (
                      <>
                        {job.completed_devices}/{job.total_devices} cameras provisioned
                        {job.failed_devices > 0 && ` · ${job.failed_devices} failed`}
                        {job.started_at && ` · Duration: ${formatDuration(job.started_at, job.completed_at)}`}
                      </>
                    ) : job.status === 'provisioning' ? (
                      <>
                        {job.completed_devices}/{job.total_devices} cameras in progress
                        {job.failed_devices > 0 && ` · ${job.failed_devices} failed`}
                      </>
                    ) : (
                      <>{job.total_devices} cameras to provision</>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {job.status === 'pending' && (
                    <button
                      onClick={() => setViewingJobId(job.id)}
                      style={{
                        padding: '5px 12px', fontSize: 12, fontWeight: 500,
                        background: 'var(--primary)', color: '#fff', border: 'none',
                        borderRadius: 6, cursor: 'pointer',
                      }}
                    >
                      Start Discovery
                    </button>
                  )}
                  {(job.status === 'discovered' || job.status === 'matched') && (
                    <button
                      onClick={() => setViewingJobId(job.id)}
                      style={{
                        padding: '5px 12px', fontSize: 12, fontWeight: 500,
                        background: 'var(--primary)', color: '#fff', border: 'none',
                        borderRadius: 6, cursor: 'pointer',
                      }}
                    >
                      View Matches
                    </button>
                  )}
                  {(job.status === 'provisioning' || job.status === 'completed' || job.status === 'failed') && (
                    <button
                      onClick={() => setViewingJobId(job.id)}
                      style={{
                        padding: '5px 12px', fontSize: 12, fontWeight: 500,
                        background: 'var(--bg)', border: '1px solid var(--border)',
                        borderRadius: 6, cursor: 'pointer', color: 'var(--text-secondary)',
                      }}
                    >
                      View Report
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
